import { useState } from "react";
import useRefCallback from "use-ref-callback";
import LabelList, { SActionButton } from "@atlas/uicorn/lib/Display/LabelList";
import Input from "@atlas/uicorn/lib/Input/Input";
import Label from "@atlas/uicorn/lib/Display/Label";
import PlusThickIcon from "@atlas/uicorn/lib/Icon/PlusThickIcon";
import Popover from "@atlas/uicorn/lib/Display/Popover";
import Button from "@atlas/uicorn/lib/Control/Button";
import Text from "@atlas/uicorn/lib/Display/Text";
import IconButton from "@atlas/uicorn/lib/Control/IconButton";
import WarningIcon from "@atlas/uicorn/lib/Icon/WarningIcon";
import isEmail from "@atlas/tool/box/type/isEmail";
import styled from "styled-components";

function CcField({ emails, onChange, disabled }: TCcFieldProps) {
  const [newCustomerEmail, setNewCustomerEmail] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleEmailValueChange = useRefCallback((event) => {
    setNewCustomerEmail(event.target.value);
    if (error) setError("");
  });

  const clearEmailValue = useRefCallback(() => {
    setNewCustomerEmail("");
    setError("");
  });

  const handleRemove = useRefCallback((email) => {
    const newEmails = emails.filter((e) => e !== email);
    onChange(newEmails).then(clearEmailValue);
  });

  const handleKeyDown = useRefCallback((event) => {
    const newEmail = newCustomerEmail.trim();
    if (event.key !== "Enter" || !newEmail) return;
    if (!isEmail(newEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    onChange([...emails, newEmail]).then(clearEmailValue);
  });

  return (
    <LabelList>
      {emails.map((email) => (
        <Popover
          key={email}
          trigger={["persistentHover", "click"]}
          placement="top-start"
          content={<Text p>{email}</Text>}
          raise
        >
          <SLabel onRemove={() => !disabled && handleRemove(email)}>
            {email!}
          </SLabel>
        </Popover>
      ))}
      <Popover
        key={emails.length}
        trigger="click"
        placement="bottom"
        onClose={clearEmailValue}
        content={
          <SInput
            autoFocus
            type="email"
            placeholder="Enter email..."
            value={newCustomerEmail}
            disabled={disabled}
            onChange={handleEmailValueChange}
            onKeyDown={handleKeyDown}
            suffix={
              error ? (
                <IconButton
                  tooltip={error}
                  size={20}
                  icon={<WarningIcon color="red6" />}
                />
              ) : (
                <SSpacer />
              )
            }
          />
        }
      >
        {emails?.length === 0 ? (
          <Button type="plain" prefix={<PlusThickIcon color="grey8" />}>
            <Text color="grey8">Add...</Text>
          </Button>
        ) : (
          <SActionButton tooltip="Add CC" icon={<PlusThickIcon />} />
        )}
      </Popover>
    </LabelList>
  );
}

export default CcField;

// #region Types

export type TCcFieldProps = {
  emails: string[];
  onChange: (emails: string[]) => Promise<void>;
  disabled?: boolean;
};

// #endregion

// #region Styles

const SLabel = styled(Label).attrs({
  outlineColor: "grey6",
})`
  max-width: min(16rem, 100%);
  cursor: default;
`;

const SSpacer = styled.div`
  width: 2rem;
`;

export const SInput = styled(Input)`
  background: transparent;
  border: none;

  &.ant-input-affix-wrapper-focused,
  &:focus:is(:not(:disabled):not([data-disabled="true"])):focus,
  &:is(:not(:disabled):not([data-disabled="true"]))[data-focus="true"] {
    box-shadow: none;
  }
`;

// #endregion
